import React, { useState } from 'react';
import WhitePanel from '../components/WhitePanel';
import PlanSummary from '../components/PlanSummary';
import ProfileEditModal from '../components/ProfileEditModal';
import NotificationSettings from '../components/NotificationSettings';
import DashboardSetup from '../components/DashboardSetup';
import { useTheme } from '../contexts/ThemeContext';

const Dashboard = () => {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showPersonalisationModal, setShowPersonalisationModal] = useState(false);
  const { theme } = useTheme();

  const handleProfileClick = () => {
    setShowProfileModal(true);
  };

  const handleNotificationClick = () => {
    setShowNotificationModal(true);
  };

  const handlePersonalisationClick = () => {
    setShowPersonalisationModal(true);
  };

  return (
    <div style={{ 
      padding: 40, 
      backgroundColor: 'var(--branding-app-surface)', 
      minHeight: '100vh' 
    }}>
      {/* Plan summary banner */}
      <PlanSummary />

      {/* Main content panel */}
      <div style={{
        background: 'var(--cc-card-bg)',
        borderRadius: 16,
        padding: 32,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        marginBottom: 32
      }}>
        {/* Action tiles grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 24
        }}>
          {/* Profile tile */}
          <WhitePanel
            title="Profile"
            description="View and edit your profile information and preferences."
            onClick={handleProfileClick}
          />

          {/* Notification settings tile */}
          <WhitePanel
            title="Notification settings"
            description="Configure push notifications and alerts."
            onClick={handleNotificationClick}
          />

          {/* Personalisation tile */}
          <WhitePanel
            title="Personalisation"
            description="Customize your dashboard layout and appearance."
            onClick={handlePersonalisationClick}
          />
        </div>
      </div>

      {/* Profile Edit Modal */}
      <ProfileEditModal 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />

      {/* Settings Modal */}
      {showNotificationModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'var(--cc-card-bg)', borderRadius: 12, width: '90%', maxWidth: 900, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', position: 'relative', transform: 'translateY(-2%)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '28px 32px', borderBottom: `1px solid var(--branding-app-surface-panel-border)`, backgroundColor: 'var(--branding-app-surface-panel)', position: 'sticky', top: 0, zIndex: 1 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: 'var(--cc-text-primary)', letterSpacing: '-0.025em' }}>
                  Notification settings
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: 14, color: 'var(--cc-text-muted)' }}>
                  Manage your notification preferences and alerts.
                </p>
              </div>
              <button
                onClick={() => setShowNotificationModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--cc-text-muted)', cursor: 'pointer', padding: '8px 12px', borderRadius: 8, transition: 'all 0.2s ease', marginLeft: 16 }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = 'var(--cc-active-bg)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; }}
              >×</button>
            </div>
            <div style={{ padding: '24px 32px 32px 32px', minHeight: '400px' }}>
              <NotificationSettings />
            </div>
          </div>
        </div>
      )}

      {/* Personalisation Modal */}
      {showPersonalisationModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '40px',
            overflow: 'auto'
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowPersonalisationModal(false); }}
        >
          <div style={{
            background: theme === 'dark' ? '#111111' : '#fff',
            borderRadius: 12,
            width: '100%',
            maxWidth: 900,
            maxHeight: 'calc(100vh - 80px)',
            boxShadow: theme === 'dark' ? '0 25px 50px -12px rgba(0,0,0,0.6)' : '0 25px 50px -12px rgba(0,0,0,0.25)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            margin: 'auto',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '28px 32px',
              borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
              backgroundColor: 'var(--branding-app-surface-panel)',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              flexShrink: 0
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: 'var(--branding-app-surface-panel-fg)', letterSpacing: '-0.025em' }}>
                  Personalisation
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: 14, color: 'var(--branding-app-surface-panel-muted)' }}>
                  Dashboard layout and map defaults
                </p>
              </div>
              <button
                onClick={() => setShowPersonalisationModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  color: 'var(--branding-app-surface-panel-muted)',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: 8,
                  transition: 'all 0.2s ease',
                  marginLeft: 16
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--branding-app-surface)';
                  e.target.style.color = 'var(--branding-app-surface-panel-sub)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'var(--branding-app-surface-panel-muted)';
                }}
              >
                ×
              </button>
            </div>

            {/* Tab Content - Scrollable */}
            <div style={{ padding: '32px', overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0 }}>
              <DashboardSetup />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;